import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Folder,
  Inbox,
  Forward,
  Mail,
  MailOpen,
  PenSquare,
  Reply,
  ReplyAll,
  Maximize2,
  MessagesSquare,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  CheckCircle2,
  Flag,
  ListFilter,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import EmailBody from '../components/EmailBody';
import { apiMessage } from './api';
import Activity from './Activity';
import Attachments from './Attachments';
import Composer from './Composer';
import Overview from './Overview';
import { MoveControls, UndoBar, canMoveInto, folderChoices, moveError } from './MoveControls';

/**
 * Mailbox — the live service mailbox, folder by folder.
 *
 * Read from Microsoft Graph by the service inbox (/api/inbox/mailbox), not from
 * its database, so it shows where mail actually is: what the automation filed,
 * and anything a person moved by hand since. Nobody on the automation side has
 * Outlook access to this mailbox, which is what this page is for.
 *
 * Its changes to the mailbox need mailbox_core's write routes and are switched
 * on by prop. `readState`: opening an unread message marks it read, and the
 * reading pane has Mark read / Mark unread. `moves`: Archive and Move to… on
 * the open message, or a list row dragged onto a folder in the tree, one at a
 * time, with Undo (see MoveControls.js). Each change
 * is recorded against the signed-in person. There is no delete. `activity`
 * shows that record (Activity.js) below the message; it is a read, so a client
 * can show it to people who cannot change mail. A client's plugin may write elsewhere (DelAir: ServiceTitan
 * tasks and bookings, each for the email's matched customer).
 *
 * Each message also carries what the service inbox recorded for it, if anything:
 * category and customer match, joined server-side on the Message-ID (Graph's own
 * id changes on every move).
 *
 * Part of the service_inbox feature, like Classification Review: admins, and
 * anyone granted Service Inbox in User Management. The API enforces the same
 * with a 403. It shows every message in the mailbox, bodies included.
 */

const PAGE_SIZE = 50;

// The mailbox reloads itself this often: folder counts and the page on screen,
// never the open message (so a task half filled in is left alone).
const AUTO_REFRESH_MS = 60 * 1000;

// Column widths on wide screens (xl), dragged by the dividers between them and
// remembered per browser. The reading pane takes whatever is left, and keeps at
// least READING_MIN of it.
const WIDTHS_KEY = 'mailbox.columnWidths';
const DEFAULT_WIDTHS = { folders: 250, list: 400 };
const FOLDERS_RANGE = [160, 480];
const LIST_MIN = 280;
const READING_MIN = 360;

const loadWidths = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(WIDTHS_KEY));
    if (saved && Number.isFinite(saved.folders) && Number.isFinite(saved.list)) return saved;
  } catch {
    /* no storage (private window) or junk in it: use the defaults */
  }
  return DEFAULT_WIDTHS;
};

const saveWidths = (w) => {
  try {
    localStorage.setItem(WIDTHS_KEY, JSON.stringify(w));
  } catch {
    /* not remembered, which is fine */
  }
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// A vertical divider between two columns. Drag it, use the arrow keys when it
// has focus, or double-click to put the default back.
const Splitter = ({ label, onDrag, onDragEnd, onReset, onStep }) => {
  const start = useRef(null);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      className="group hidden cursor-col-resize touch-none select-none items-stretch justify-center outline-none xl:flex"
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = e.clientX;
      }}
      onPointerMove={(e) => {
        if (start.current != null) onDrag(e.clientX - start.current);
      }}
      onPointerUp={(e) => {
        if (start.current == null) return;
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
        onDragEnd();
      }}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          onStep(e.key === 'ArrowLeft' ? -16 : 16);
        }
      }}
    >
      <div className="my-2 w-1 rounded-full bg-transparent transition-colors group-hover:bg-blue-300 group-focus-visible:bg-blue-400" />
    </div>
  );
};

// Filters. The category comes from the service inbox's database, not from
// Graph, so the service reads the whole folder (up to a cap of several thousand
// messages, newest first) and pages through the matches: a filtered list is
// "1–50 of 132 matches", paged like any other.
// `from` / `to` are days (YYYY-MM-DD, both included) in the reader's time zone:
// the received window the Ops Dashboard links in with.
const NO_FILTERS = { q: '', from: '', to: '', flag: '' };

const parseDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const dayAfter = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

const receivedLabel = (from, to) => {
  const a = parseDay(from);
  const b = parseDay(to);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (!a) return '';
  return !b || from === to ? `Received ${fmt(a)}` : `Received ${fmt(a)} – ${fmt(b)}`;
};

// What the service said about a filtered page: `total_matches` stands in for
// the folder's total, and `complete` is false when the folder was past the
// service's cap, so only its newest `scanned` messages were filtered.
const NO_PAGE_INFO = { filtered: false, total_matches: 0, scanned: 0, complete: true };

// An all-folders search's results, newest first, a page at a time: the index
// gives no total, only whether there is more (`next_cursor`).
const NO_FOUND = {
  query: '',
  messages: [],
  next_cursor: null,
  loading: false,
  since: null,
  error: null,
};

const filtersActive = (f, plugin) =>
  Boolean(f.q.trim() || f.from || f.flag || (plugin.filterActive && plugin.filterActive(f)));

// The filters in force, in words, for "No messages match …": the mailbox's
// own, then whatever the plugin names for its own.
export const filterWords = (f, plugin) => {
  const words = [];
  if (f.q.trim()) words.push(`“${f.q.trim()}”`);
  if (f.from) words.push(f.to && f.to !== f.from ? `${f.from} to ${f.to}` : f.from);
  if (f.flag) words.push(f.flag === 'complete' ? 'Completed' : 'Flagged');
  if (plugin.filterLabels) words.push(...plugin.filterLabels(f));
  return words;
};

// Only what is set: the mailbox's own filters, then the plugin's.
const toQuery = (f, plugin) => {
  const from = parseDay(f.from);
  const to = parseDay(f.to) || from;
  return {
    q: f.q,
    since: from || undefined,
    until: to ? dayAfter(to) : undefined,
    flag: f.flag || undefined,
    ...(plugin.filterQuery ? plugin.filterQuery(f) : {}),
  };
};

// The search bar, laid out like a mail client's: one box with where it looks
// chosen inside it (this folder: the folder read, which the other filters
// combine with; all folders: the provider's index, which searches bodies too
// but takes no other filter), and one Filter button opening a panel with the
// rest. What is in force shows as chips under the box, each with its own ×.
const SCOPE_LABEL = { folder: 'This folder', all: 'All folders' };

// The filters in force as removable chips: the mailbox's own, then the plugin's
// (`filterChips`, or its words from `filterLabels` without a ×).
const filterChips = (f, plugin, onChange) => {
  const chips = [];
  if (f.flag) {
    chips.push({
      key: 'flag',
      label: f.flag === 'complete' ? 'Completed' : 'Flagged',
      clear: () => onChange({ flag: '' }),
      clearLabel: 'Any flag',
    });
  }
  if (f.from) {
    chips.push({
      key: 'date',
      label: receivedLabel(f.from, f.to),
      clear: () => onChange({ from: '', to: '' }),
      clearLabel: 'Any date',
    });
  }
  if (plugin.filterChips) {
    plugin.filterChips(f).forEach((c, i) =>
      chips.push({ key: `plugin-${i}`, ...c, clear: () => onChange(c.clear) })
    );
  } else if (plugin.filterLabels) {
    plugin.filterLabels(f).forEach((label, i) => chips.push({ key: `plugin-${i}`, label }));
  }
  return chips;
};

const FilterBar = ({
  filters,
  search,
  controls,
  plugin,
  scope,
  onScope,
  onSearch,
  onChange,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const panel = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (panel.current && !panel.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // Across all folders only the flag applies (see the comment on the panel).
  const chips = filterChips(scope === 'all' ? { ...filters, from: '' } : filters, plugin, onChange);
  const active = chips.length;
  return (
    <div className="border-b border-gray-200 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <div className="flex min-w-0 flex-1 items-center rounded-md border border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
          <Search className="ml-2 h-3.5 w-3.5 shrink-0 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={scope === 'all' ? 'Search all folders' : 'Search this folder'}
            aria-label="Search"
            className="min-w-0 flex-1 bg-transparent py-1 pl-1.5 pr-1 text-sm placeholder-gray-400 focus:outline-none"
          />
          <select
            aria-label="Search in"
            value={scope}
            onChange={(e) => onScope(e.target.value)}
            title={
              scope === 'all'
                ? 'Every folder, message text included'
                : 'This folder: sender, subject and text'
            }
            className="max-w-[7.5rem] shrink-0 cursor-pointer rounded-r-md border-l border-gray-200 bg-gray-50 py-1 pl-2 pr-6 text-xs text-gray-700 focus:outline-none"
          >
            {Object.entries(SCOPE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="relative shrink-0" ref={panel}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
              active
                ? 'border-blue-300 bg-blue-50 text-blue-800'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ListFilter className="h-3.5 w-3.5" />
            Filter
            {active > 0 && (
              <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold leading-4 text-white">
                {active}
              </span>
            )}
          </button>
          {open && (
            <div
              role="dialog"
              aria-label="Filters"
              className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-lg"
            >
              <label className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-xs text-gray-500">Flag</span>
                {/* Flagged works in both scopes: the provider filters a folder, the
                    index answers across folders. Completed is a folder filter only:
                    the index has no way to find completed flags (checked
                    2026-09-29), and the mailbox-wide filter that could is the
                    12-second kind. */}
                <select
                  aria-label="Flag filter"
                  value={filters.flag}
                  onChange={(e) => onChange({ flag: e.target.value })}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm text-gray-700"
                >
                  <option value="">Any flag</option>
                  <option value="flagged">Flagged</option>
                  <option value="complete" disabled={scope === 'all'}>
                    {scope === 'all' ? 'Completed (within a folder)' : 'Completed'}
                  </option>
                </select>
              </label>
              {scope === 'all' ? (
                <div className="mt-2 text-xs text-gray-500">
                  Other filters apply within a folder.
                </div>
              ) : (
                controls && <div className="mt-2 flex flex-col gap-2">{controls}</div>
              )}
              {(active > 0 || search.trim() || scope === 'all') && (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      onClear();
                      setOpen(false);
                    }}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {active > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex max-w-[16rem] items-center gap-0.5 rounded-full bg-blue-50 py-0.5 pl-2.5 pr-1 text-xs text-blue-800 ring-1 ring-inset ring-blue-200"
            >
              <span className="truncate">{c.label}</span>
              {c.clear && (
                <button
                  type="button"
                  onClick={c.clear}
                  className="rounded-full p-0.5 hover:bg-blue-100"
                  aria-label={c.clearLabel || `Remove ${c.label}`}
                  title={c.clearLabel || 'Remove'}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            onClick={onClear}
            className="ml-1 text-xs text-gray-500 hover:text-gray-800 hover:underline"
            aria-label="Clear filters"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};


// Where you were is kept in the URL (?folder=&message=&q=&category=), so a
// refresh, or signing back in (which returns to the last page), puts you back
// on the same folder, message and filters. Expanded subfolders are kept per
// browser, so a folder inside another one can be found again on reload.
const EXPANDED_KEY = 'mailbox.expandedFolders';
const loadExpanded = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(EXPANDED_KEY));
    return new Set(Array.isArray(ids) ? ids.filter((i) => typeof i === 'string') : []);
  } catch {
    return new Set();
  }
};

// Full screen is remembered per browser too, so a reload comes back to it. Only
// from md up, where the toggle is drawn: below that there is no button (and on a
// phone no Esc) to leave it by.
const FULLSCREEN_KEY = 'mailbox.fullScreen';
const loadFullScreen = () => {
  try {
    return localStorage.getItem(FULLSCREEN_KEY) === '1' && window.innerWidth >= 768;
  } catch {
    return false;
  }
};

// The folder tree can be hidden on wide screens to give the list and the
// message the room, remembered per browser. The folder dropdown then stands in
// for it, as it does below xl.
const FOLDERS_SHOWN_KEY = 'mailbox.foldersShown';
const loadFoldersShown = () => {
  try {
    return localStorage.getItem(FOLDERS_SHOWN_KEY) !== '0';
  } catch {
    return true;
  }
};

// A plugin may name the folders its own work lives in (`loadFolderScope`), and
// the tree can then show only those plus the Inbox and Drafts, matched on the
// display name. On unless turned off, and remembered per browser.
const EVERYDAY_FOLDERS = ['inbox', 'drafts'];
const SCOPED_KEY = 'mailbox.scopedFolders';
const loadScoped = () => {
  try {
    return localStorage.getItem(SCOPED_KEY) !== '0';
  } catch {
    return true;
  }
};

// Inbox first, then Graph's own (alphabetical) order.
const sortTop = (folders) =>
  [...folders].sort((a, b) => {
    const ai = a.name.toLowerCase() === 'inbox' ? 0 : 1;
    const bi = b.name.toLowerCase() === 'inbox' ? 0 : 1;
    return ai - bi;
  });

// Always the received time; the date too unless it came in today, and the year
// only when it is not this one.
const fmtListDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return time;
  const date = d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${date}, ${time}`;
};

const fmtFullDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// Seconds since `since`, ticking once a second while mounted.
const useElapsed = (since) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  return Math.max(0, Math.floor((now - since) / 1000));
};

// A list load a person is waiting on: a sliding bar along the top of the list,
// and, over rows already on screen, a pill saying what it is doing. A filter
// reads the whole folder, so it counts the seconds and says why it is slow.
const ListProgress = ({ wait, folder, overlay }) => {
  const secs = useElapsed(wait.since);
  const what = wait.everywhere
    ? 'Searching every folder…'
    : wait.filtered
    ? `Searching ${folder?.name || 'the folder'}…`
    : `Loading ${folder?.name || 'messages'}…`;
  return (
    <>
      <div className="sticky top-0 z-20 h-0.5 overflow-hidden bg-blue-100" role="progressbar" aria-label={what}>
        <div className="mailbox-progress h-full bg-blue-600" />
      </div>
      <div className="pointer-events-none sticky top-2 z-20 flex h-0 justify-center">
        {(overlay || wait.filtered) && (
          <span className="inline-flex h-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-sm text-gray-700 shadow ring-1 ring-gray-200">
            <RefreshCw className="h-3.5 w-3.5 animate-spin text-blue-600" />
            {what}
            {secs >= 1 && <span className="tabular-nums text-gray-500">{secs}s</span>}
          </span>
        )}
      </div>
      {wait.filtered && !wait.everywhere && secs >= 3 && !overlay && (
        <div className="px-3 pt-12 text-center text-xs text-gray-500">
          A filter reads the whole folder, so a big one takes a few seconds.
        </div>
      )}
    </>
  );
};

const Bar = ({ className }) => <div className={`rounded bg-gray-200 ${className}`} />;

// Placeholder rows shaped like the real ones, while a folder's first page loads.
const SkeletonRows = ({ count = 8 }) => (
  <div className="animate-pulse" aria-hidden="true">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="space-y-1.5 border-b border-gray-100 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Bar className={`h-3.5 ${i % 3 === 0 ? 'w-1/3' : i % 3 === 1 ? 'w-2/5' : 'w-1/4'}`} />
          <div className="flex-1" />
          <Bar className="h-3 w-12" />
        </div>
        <Bar className={`h-3.5 ${i % 2 ? 'w-3/4' : 'w-2/3'}`} />
        <Bar className="h-3 w-11/12" />
        <div className="flex gap-1.5 pt-0.5">
          <Bar className="h-4 w-20 rounded-full" />
          {i % 2 === 0 && <Bar className="h-4 w-28 rounded-full" />}
        </div>
      </div>
    ))}
  </div>
);

// The reading pane while a message loads.
// The flag beside the subject, an icon only: its state is its colour, and
// the words are its label. With mail rights an unflagged message is flagged
// in one click; a flagged or completed one opens its choices.
function FlagControl({ message, canSet, onSet }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const { flag } = message;
  const due = flag === 'flagged' && message.flag_due ? `due ${fmtDay(message.flag_due)}` : '';
  const label =
    flag === 'flagged' ? `Flagged${due ? ` · ${due}` : ''}` : flag === 'complete' ? 'Flag complete' : 'Flag';
  if (!canSet && flag !== 'flagged' && flag !== 'complete') return null;
  const icon =
    flag === 'complete' ? (
      <CheckCircle2 className="h-4 w-4 text-green-600" />
    ) : (
      <Flag className={`h-4 w-4 ${flag === 'flagged' ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
    );
  const dueText = due && <span className="text-xs text-red-700">{due}</span>;
  if (!canSet) {
    return (
      <span title={label} aria-label={label} className="inline-flex shrink-0 items-center gap-1 px-1 py-1.5">
        {icon}
        {dueText}
      </span>
    );
  }
  const choices =
    flag === 'flagged'
      ? [
          ['complete', 'Mark complete'],
          ['none', 'Clear flag'],
        ]
      : [
          ['flagged', 'Flag again'],
          ['none', 'Clear flag'],
        ];
  return (
    <div ref={box} className="relative inline-flex shrink-0 items-center gap-1">
      <button
        type="button"
        title={label}
        aria-label={label}
        aria-haspopup={flag ? 'menu' : undefined}
        onClick={() => (flag === 'flagged' || flag === 'complete' ? setOpen((o) => !o) : onSet('flagged'))}
        className="rounded p-1.5 hover:bg-gray-100"
      >
        {icon}
      </button>
      {dueText}
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 min-w-[9rem] rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
        >
          {choices.map(([value, text]) => (
            <button
              key={value}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSet(value);
              }}
              className="block w-full px-3 py-1.5 text-left text-gray-700 hover:bg-gray-50"
            >
              {text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const MessageSkeleton = () => (
  <div className="animate-pulse space-y-3" aria-hidden="true">
    <div className="rounded-md border border-gray-200 border-l-4 bg-gray-50 p-3">
      <div className="flex gap-2">
        <Bar className="h-5 w-32 rounded-full" />
        <Bar className="h-5 w-40 rounded-full" />
      </div>
    </div>
    <Bar className="h-6 w-2/3" />
    <div className="space-y-1.5">
      <Bar className="h-3.5 w-1/2" />
      <Bar className="h-3.5 w-2/5" />
      <Bar className="h-3.5 w-1/3" />
    </div>
    <div className="space-y-2 pt-3">
      {['w-full', 'w-11/12', 'w-full', 'w-4/5', 'w-full', 'w-3/5'].map((w, i) => (
        <Bar key={i} className={`h-3.5 ${w}`} />
      ))}
    </div>
  </div>
);

// The whole conversation a message belongs to, from every folder, oldest first.
// Kept while another message of the same thread loads, so switching between
// them does not re-read it; a different thread reads again.
const useConversation = (api, conversationId) => {
  const [state, setState] = useState({ id: null, list: null, error: null });
  // The thread last asked for. An answer for any other is dropped; a message
  // with no thread (or one still loading) does not cancel it.
  const wanted = useRef(null);
  useEffect(() => {
    if (!conversationId || conversationId === wanted.current) return;
    wanted.current = conversationId;
    setState({ id: conversationId, list: null, error: null });
    api.getConversation(conversationId)
      .then((list) => {
        if (wanted.current === conversationId) setState({ id: conversationId, list, error: null });
      })
      .catch((err) => {
        if (wanted.current !== conversationId) return;
        setState({
          id: conversationId,
          list: null,
          error: apiMessage(err, 'Could not read the rest of this conversation.'),
        });
      });
  }, [api, conversationId]);
  return state;
};

// Where a thread entry is filed now, or that it is gone.
const ThreadFolder = ({ m }) => {
  if (m.in_mailbox === false) {
    return <span className="shrink-0 text-xs italic text-gray-400">no longer in the mailbox</span>;
  }
  if (!m.folder_name) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-px text-xs text-gray-700">
      <Folder className="h-3 w-3" />
      {m.folder_name}
    </span>
  );
};

// Another message of the open one's conversation, collapsed: who, when, which
// folder it is in now and what it was classified as. Opens it.
// The open message is drawn the same way, highlighted, and does nothing.
const ThreadRow = ({ m, onOpen, current = false, Badge = null }) => (
  <button
    type="button"
    onClick={() => !current && onOpen(m)}
    disabled={!current && m.in_mailbox === false}
    aria-current={current || undefined}
    className={`block w-full border-b border-l-4 border-b-gray-200 px-3 py-2 text-left ${
      current
        ? 'cursor-default border-l-blue-500 bg-blue-50'
        : 'border-l-transparent hover:bg-gray-50 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent'
    }`}
  >
    <div className="flex items-center gap-2 text-sm">
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className="truncate font-medium text-gray-900">{who(m.sender)}</span>
      {Badge && <Badge message={m} />}
      <span className="ml-auto shrink-0 text-xs text-gray-500">{fmtListDate(m.received_at)}</span>
    </div>
    <div className="flex items-center gap-2 pl-5 text-sm text-gray-500">
      <span className="min-w-0 flex-1 truncate">{m.preview || m.subject || '(no subject)'}</span>
      <ThreadFolder m={m} />
    </div>
  </button>
);

// How big a row's conversation is: every stored message of it, in any folder
// (the service's `thread_count`), or at least how many are on this page. When
// more of it is on this page, the chip also expands them beneath the row.
const ThreadChip = ({ size, onPage, open, onToggle }) => {
  const className =
    'inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-px text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300';
  const label = (
    <>
      <MessagesSquare className="h-3 w-3" />
      {size}
    </>
  );
  if (onPage < 2) {
    return (
      <span className={className} title={`${size} messages in this conversation; open it to see them`}>
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-expanded={open}
      title={`${size} messages in this conversation, ${onPage} of them on this page`}
      className={`${className} hover:bg-gray-200`}
    >
      {label}
      <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );
};

// A page's messages as conversations, newest first: each group is one thread's
// messages on this page, its newest first. A message with no conversation id
// is a group of its own. Only what is on the page: older messages of a thread
// may be on another page or in another folder, which the reading pane shows.
export const groupByConversation = (messages) => {
  const groups = new Map();
  messages.forEach((m) => {
    const key = m.conversation_id || `id:${m.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  });
  return [...groups.values()];
};

const who = (a) => (a ? a.name || a.address || '(unknown)' : '(unknown)');
// "Jane Kelly <jk.2011@ymail.com>"; the address alone when the name is empty or
// is just the address again.
// Who a list row names: the sender, except for mail the mailbox itself sent or
// is drafting (a draft has no sender yet), where that says nothing and the
// recipients do. Keyed on the sender, not the folder's name, so it holds in a
// search and in any language.
export const rowParty = (m, mailbox) => {
  const from = (m.sender?.address || '').trim().toLowerCase();
  const ours = !from || (mailbox && from === mailbox.trim().toLowerCase());
  if (!ours) return { label: whoFull(m.sender) || '(unknown)', title: whoFull(m.sender) };
  const to = m.to || [];
  if (!to.length) return { label: 'To: (no recipients)', title: '' };
  const all = to.map(whoFull).join(', ');
  const first = whoFull(to[0]);
  return { label: `To: ${first}${to.length > 1 ? `, +${to.length - 1}` : ''}`, title: `To: ${all}` };
};

// A flag's due date, as a day: Graph sends it as a local date-time.
const fmtDay = (iso) => {
  const d = parseDay((iso || '').slice(0, 10));
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};

const whoFull = (a) => {
  const name = (a?.name || '').trim();
  const address = (a?.address || '').trim();
  if (name && address && name.toLowerCase() !== address.toLowerCase()) {
    return `${name} <${address}>`;
  }
  return address || name || '';
};

// The folder tree as a dropdown, below xl where there is no room for the tree.
// Top-level folders, then each one's subfolders indented beneath it.
const FolderPicker = ({ roots, subfolders, current, onOpen, onPick, wide = false }) => {
  const options = [];
  for (const f of roots) {
    options.push({ f, depth: 0 });
    for (const c of subfolders[f.id] || []) options.push({ f: c, depth: 1 });
  }
  if (current && !options.some((o) => o.f.id === current.id)) {
    options.push({ f: current, depth: 0 });
  }
  return (
    <select
      value={current?.id || ''}
      onFocus={onOpen}
      onChange={(e) => onPick(e.target.value)}
      aria-label="Folder"
      className={`min-w-0 max-w-[60%] truncate rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm font-medium text-gray-800 ${
        wide ? '' : 'xl:hidden'
      }`}
    >
      {options.map(({ f, depth }) => (
        <option key={f.id} value={f.id}>
          {depth ? '\u00a0\u00a0\u00a0' : ''}
          {f.name}
          {f.unread ? ` (${f.unread})` : ''}
        </option>
      ))}
    </select>
  );
};

// `drop`, while a message is being dragged: whether this folder takes it
// (`accepts`), whether it is under the pointer (`over`), and the handlers.
// A folder that does not take it never calls preventDefault, so the browser
// shows the drop as refused there.
const FolderRow = ({ folder, depth, expanded, selected, onToggle, onSelect, drop }) => {
  const isInbox = folder.name.toLowerCase() === 'inbox';
  const Icon = isInbox ? Inbox : Folder;
  const target = drop && drop.accepts;
  const dropHandlers = drop
    ? {
        onDragEnter: (e) => {
          if (!target) return;
          e.preventDefault();
          drop.onEnter(folder);
        },
        onDragOver: (e) => {
          if (!target) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        },
        onDragLeave: (e) => {
          // Moving onto the row's own icon or count is not leaving it.
          if (e.currentTarget.contains(e.relatedTarget)) return;
          drop.onLeave(folder);
        },
        onDrop: (e) => {
          if (!target) return;
          e.preventDefault();
          drop.onDrop(folder);
        },
      }
    : {};
  return (
    <div
      className={`flex items-center rounded px-1 py-1 text-sm cursor-pointer ${
        drop && drop.over
          ? 'bg-blue-100 text-blue-900 ring-2 ring-inset ring-blue-400'
          : selected
          ? 'bg-blue-100 text-blue-900'
          : 'hover:bg-gray-100 text-gray-800'
      }`}
      style={{ paddingLeft: `${depth * 14 + 4}px` }}
      onClick={() => onSelect(folder)}
      {...dropHandlers}
    >
      <button
        type="button"
        className="w-4 h-4 mr-1 flex items-center justify-center text-gray-400"
        onClick={(e) => {
          e.stopPropagation();
          if (folder.child_count) onToggle(folder);
        }}
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        {folder.child_count ? (
          expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
        ) : null}
      </button>
      <Icon className="h-4 w-4 mr-1.5 shrink-0 text-gray-500" />
      <span className={`truncate flex-1 ${folder.unread ? 'font-semibold' : ''}`}>{folder.name}</span>
      {folder.unread > 0 && (
        <span className="ml-2 text-xs font-semibold text-blue-700">{folder.unread}</span>
      )}
      <span className="ml-2 text-xs text-gray-400 tabular-nums">{folder.total}</span>
    </div>
  );
};

// Which message the reading pane is showing, for React keys: the RFC-822
// Message-ID, which a move leaves alone, so a move (a new Graph id) keeps the
// plugin card and its half-filled forms rather than mounting a fresh one. Two
// siblings must never share a key: with the Graph id on both, every move left
// one more card on screen (2026-09-29).
const messageKey = (m) => m.internet_message_id || m.id;

const MailboxPage = ({
  api,
  plugin,
  readState = false,
  moves = false,
  activity: showActivity = false,
  compose = false,
  flags = false,
}) => {
  const [mailbox, setMailbox] = useState('');
  const [roots, setRoots] = useState([]);
  const [children, setChildren] = useState({}); // folder id -> Folder[]
  const [params, setParams] = useSearchParams();
  // What the URL asked for when the page opened, restored once the tree loads.
  // `folderName` (from the Ops Dashboard) picks a folder by name when no id is given.
  // `mid` (a Message-ID, from a ServiceTitan task's description) finds the
  // message wherever it has been filed since.
  const restore = useRef({
    folder: params.get('folder'),
    folderName: params.get('folderName'),
    message: params.get('message'),
    mid: params.get('mid'),
  });
  const [expanded, setExpanded] = useState(loadExpanded);
  const [treeError, setTreeError] = useState(null);
  const [loadingTree, setLoadingTree] = useState(true);

  const [folder, setFolder] = useState(null);
  const [offset, setOffset] = useState(0);
  const [messages, setMessages] = useState([]);
  const [listError, setListError] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  // A list load a person asked for (not the minute's quiet refresh): when it
  // started, and whether it is a filter, which reads the whole folder.
  const [listWait, setListWait] = useState(null);

  // What the plugin loads once for its filter controls (DelAir: categories).
  const [filterOptions, setFilterOptions] = useState(null);
  const noFilters = { ...NO_FILTERS, ...(plugin.filterDefaults || {}) };
  const [filters, setFilters] = useState(() => ({
    ...noFilters,
    q: params.get('q') || '',
    // `flagged=1` is the older spelling of flag=flagged.
    flag: ['flagged', 'complete'].includes(params.get('flag'))
      ? params.get('flag')
      : params.get('flagged') === '1'
      ? 'flagged'
      : '',
    from: parseDay(params.get('from')) ? params.get('from') : '',
    to: parseDay(params.get('to')) ? params.get('to') : '',
    ...(plugin.filtersFromUrl ? plugin.filtersFromUrl(params) : {}),
  }));
  // The box; filters.q lags it by a debounce.
  const [search, setSearch] = useState(() => params.get('q') || '');
  // 'all': the box searches every folder through the provider's index, and the
  // list shows those results (`found`) instead of the folder's page.
  const [scope, setScope] = useState(() => (params.get('scope') === 'all' ? 'all' : 'folder'));
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const [found, setFound] = useState(NO_FOUND);
  const searchToken = useRef(0);
  // What the service said about a filtered page (see NO_PAGE_INFO).
  const [pageInfo, setPageInfo] = useState(NO_PAGE_INFO);

  // Column widths, and the widths a drag started from (a drag reports how far
  // it has moved from where it began, not from the last move).
  const [widths, setWidths] = useState(loadWidths);
  const [dragging, setDragging] = useState(false);
  const dragFrom = useRef(null);
  const gridRef = useRef(null);

  // Full screen: the page covers the app's navigation, and Esc gives it back.
  const [fullScreen, setFullScreen] = useState(loadFullScreen);
  const [foldersShown, setFoldersShown] = useState(loadFoldersShown);
  // The plugin's folder names (null until loaded, or with no plugin list), and
  // whether the tree shows only those.
  const [folderScope, setFolderScope] = useState(null);
  const [scoped, setScoped] = useState(loadScoped);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const [openId, setOpenId] = useState(null);
  // Conversations expanded in the list, by conversation id.
  const [openThreads, setOpenThreads] = useState(() => new Set());
  const [message, setMessage] = useState(null);
  // Stepping through the conversation keeps the card above it on screen,
  // dimmed, until the next message arrives, so the panel does not jump.
  const [heldCard, setHeldCard] = useState(null);
  const [messageError, setMessageError] = useState(null);
  // The conversation the thread panel is showing, and which of its messages is
  // highlighted. Kept apart from `message` so that stepping through a thread
  // leaves the panel in place and only the message below it reloads.
  // `anchor` is the message the panel was opened on: always part of it, even
  // when the stored thread does not list it (Exchange re-groups conversations
  // after the fact, and the store keeps each message's conversation as it was
  // when polled; a message can also be one the poller never saw).
  const NO_PANE = { id: null, mid: null, anchor: null };
  const [paneThread, setPaneThread] = useState(NO_PANE);
  const [readError, setReadError] = useState(null);
  // Moves (`moves`): one in flight at a time, and the last one, for Undo.
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveFailed, setMoveFailed] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  // A list row being dragged onto a folder (`moves`): the message, the folder
  // under the pointer, and a timer that opens a collapsed folder held over.
  const [dragMsg, setDragMsg] = useState(null);
  const [dropOn, setDropOn] = useState(null);
  const expandTimer = useRef(null);
  // The open message's recorded activity (who read or moved it from here).
  // Loaded when a message opens and again after a change made from this page.
  const [activity, setActivity] = useState({ key: null, list: [] });
  // The composer (`compose`): what it was opened on, and a line after it closes.
  const [composing, setComposing] = useState(null);
  const [composeNotice, setComposeNotice] = useState(null);
  const [composeStarting, setComposeStarting] = useState(false);
  const [composeError, setComposeError] = useState(null);
  // The composer, to ask it before leaving (see Composer's confirmLeave).
  const composerRef = useRef(null);
  // Run `next` now, or once the composer has asked about unsaved changes.
  const leaveComposer = (next) => {
    if (composing && composerRef.current) composerRef.current.confirmLeave(next);
    else next();
  };
  const [activityTick, setActivityTick] = useState(0);

  // Drop responses that arrive after the user has clicked somewhere else.
  const listToken = useRef(0);
  const messageToken = useRef(0);

  const loadTree = useCallback(async () => {
    setLoadingTree(true);
    setTreeError(null);
    try {
      const data = await api.getFolders();
      setMailbox(data.mailbox);
      const top = sortTop(data.folders);
      setRoots(top);
      // Refresh the open subfolder levels too, so their counts move with the rest.
      const open = [...expanded];
      const fresh = {};
      await Promise.all(
        open.map(async (id) => {
          try {
            fresh[id] = await api.getFolderChildren(id);
          } catch {
            /* a folder removed since: leave it collapsed */
          }
        })
      );
      setChildren(fresh);
      return [...top, ...Object.values(fresh).flat()];
    } catch (err) {
      setTreeError(apiMessage(err, 'Could not load the mailbox folders.'));
      return [];
    } finally {
      setLoadingTree(false);
    }
  }, [expanded]);

  const loadMessages = useCallback(async (f, off, flt, { quiet = false } = {}) => {
    // In all-folders mode the list is the search's, so the folder is not read
    // (with a filter set, that would walk the whole folder for nothing). It is
    // read again when a folder is picked or the scope set back.
    if (scopeRef.current === 'all') return;
    const token = ++listToken.current;
    setLoadingList(true);
    setListWait(quiet ? null : { since: Date.now(), filtered: filtersActive(flt, plugin) });
    setListError(null);
    try {
      const data = await api.getFolderMessages(f.id, {
        limit: PAGE_SIZE,
        offset: off,
        query: toQuery(flt, plugin),
      });
      if (token === listToken.current) {
        setMessages(data.messages);
        setPageInfo({
          filtered: Boolean(data.filtered),
          total_matches: data.total_matches || 0,
          scanned: data.scanned || 0,
          complete: data.complete !== false,
        });
      }
    } catch (err) {
      if (token === listToken.current) {
        setMessages([]);
        setPageInfo(NO_PAGE_INFO);
        setListError(apiMessage(err, 'Could not load this folder.'));
      }
    } finally {
      if (token === listToken.current) {
        setLoadingList(false);
        setListWait(null);
      }
    }
  }, []);

  // Filters carry across folders, as a search would.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const everywhere =
    scope === 'all' && (filters.q.trim().length >= 2 || filters.flag === 'flagged');

  // One page of an all-folders search: the first replaces, a cursor appends.
  const runSearch = async (q, cursor = null) => {
    const token = ++searchToken.current;
    setFound((f) => ({
      ...(cursor ? f : NO_FOUND),
      query: q,
      loading: true,
      since: Date.now(),
      error: null,
    }));
    try {
      const data = await api.search(q, { cursor, flagged: filtersRef.current.flag === 'flagged' });
      if (token !== searchToken.current) return;
      setFound((f) => ({
        ...NO_FOUND,
        query: data.query,
        messages: cursor ? [...f.messages, ...data.messages] : data.messages,
        next_cursor: data.next_cursor,
      }));
    } catch (err) {
      if (token !== searchToken.current) return;
      setFound((f) => ({
        ...f,
        loading: false,
        since: null,
        error: apiMessage(err, 'Could not search the mailbox.'),
      }));
    }
  };

  useEffect(() => {
    if (everywhere) {
      runSearch(filters.q.trim());
    } else {
      searchToken.current += 1; // drop a search still in flight
      setFound(NO_FOUND);
    }
  }, [everywhere, filters.q, filters.flag]); // eslint-disable-line

  const changeScope = (next) => {
    if (next === scopeRef.current) return;
    setScope(next);
    scopeRef.current = next;
    // Completed is a folder filter only (see FilterBar).
    if (next === 'all' && filtersRef.current.flag === 'complete') {
      const cleared = { ...filtersRef.current, flag: '' };
      setFilters(cleared);
      filtersRef.current = cleared;
    }
    setOpenId(null);
    setMessage(null);
    if (next === 'folder' && folder) {
      setOffset(0);
      loadMessages(folder, 0, filtersRef.current);
    }
  };

  // A folder picked in the tree or the phone dropdown: that folder, so an
  // all-folders search gives way to it.
  const pickFolder = (f) => leaveComposer(() => showFolder(f));
  const showFolder = (f) => {
    setScope('folder');
    scopeRef.current = 'folder';
    selectFolder(f);
  };

  const selectFolder = useCallback(
    (f) => {
      setFolder(f);
      setOffset(0);
      setOpenId(null);
      setMessage(null);
      loadMessages(f, 0, filtersRef.current);
    },
    [loadMessages]
  );

  // Any filter change starts the folder over from the newest message.
  const applyFilters = (next) => {
    setFilters(next);
    filtersRef.current = next;
    setOffset(0);
    setOpenId(null);
    setMessage(null);
    if (folder) loadMessages(folder, 0, next);
  };
  const changeFilters = (patch) => applyFilters({ ...filtersRef.current, ...patch });
  const clearFilters = () => {
    setSearch('');
    setScope('folder');
    scopeRef.current = 'folder';
    applyFilters(noFilters);
  };

  // Typing searches once it pauses, not per keystroke: each search walks Graph.
  useEffect(() => {
    if (search === filtersRef.current.q) return undefined;
    const t = setTimeout(() => changeFilters({ q: search }), 400);
    return () => clearTimeout(t);
  }, [search]); // the filters it reads are in a ref

  useEffect(() => {
    if (!plugin.loadFolderScope) return;
    plugin
      .loadFolderScope()
      .then((names) => setFolderScope(Array.isArray(names) ? names : null))
      .catch(() => setFolderScope(null));
  }, [plugin]);

  useEffect(() => {
    try {
      localStorage.setItem(SCOPED_KEY, scoped ? '1' : '0');
    } catch {
      /* not remembered (private window), which is fine */
    }
  }, [scoped]);

  useEffect(() => {
    if (!plugin.loadFilterOptions) return;
    plugin
      .loadFilterOptions()
      .then(setFilterOptions)
      .catch(() => setFilterOptions(null));
  }, [plugin]);

  // First load: the tree, then the folder and message the URL names, else
  // Needs Review (or the Inbox if there isn't one).
  useEffect(() => {
    (async () => {
      const all = await loadTree();
      if (restore.current.mid) {
        try {
          const where = await api.locate(restore.current.mid);
          restore.current.folder = where.folder_id || restore.current.folder;
          restore.current.message = where.id;
        } catch (err) {
          setMessageError(
            err?.response?.status === 404
              ? 'That email is no longer in the mailbox.'
              : apiMessage(err, 'Could not find that email.')
          );
        }
      }
      const named = (name) => all.find((f) => f.name.toLowerCase() === name);
      const asked = restore.current.folder && all.find((f) => f.id === restore.current.folder);
      const byName = restore.current.folderName && named(restore.current.folderName.toLowerCase());
      const start =
        asked || byName || (plugin.defaultFolder && named(plugin.defaultFolder)) || named('inbox');
      if (start) selectFolder(start);
      // The message too, even when its folder is not in the tree yet (a
      // subfolder nobody has expanded): the Ops Dashboard opens a message wherever
      // it has been moved to, and the id alone is enough to read it.
      if (restore.current.message) openMessage(restore.current.message, { quiet: Boolean(asked) });
      setRefreshedAt(new Date());
    })();
  }, []); // once, on mount

  // Keep the URL in step, replacing rather than pushing, so Back leaves the
  // Mailbox instead of stepping through every message opened.
  useEffect(() => {
    const next = {};
    if (folder?.id) next.folder = folder.id;
    if (openId) next.message = openId;
    if (filters.q.trim()) next.q = filters.q.trim();
    if (scope === 'all') next.scope = 'all';
    if (filters.from) next.from = filters.from;
    if (filters.to) next.to = filters.to;
    if (filters.flag) next.flag = filters.flag;
    Object.assign(next, plugin.filtersToUrl ? plugin.filtersToUrl(filters) : {});
    setParams(next, { replace: true });
  }, [folder?.id, openId, scope, JSON.stringify(filters)]); // eslint-disable-line

  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded]));
    } catch {
      /* not remembered, which is fine */
    }
  }, [expanded]);

  const toggle = async (f) => {
    const next = new Set(expanded);
    if (next.has(f.id)) {
      next.delete(f.id);
      setExpanded(next);
      return;
    }
    next.add(f.id);
    setExpanded(next);
    if (!children[f.id]) {
      try {
        const kids = await api.getFolderChildren(f.id);
        setChildren((c) => ({ ...c, [f.id]: kids }));
      } catch (err) {
        setTreeError(apiMessage(err, 'Could not load subfolders.'));
      }
    }
  };

  // Back to the list, below xl where the message replaced it.
  const closeMessage = () => {
    messageToken.current += 1; // drop a load still in flight
    setOpenId(null);
    setMessage(null);
    setHeldCard(null);
    setMessageError(null);
    setPaneThread(NO_PANE);
    setLastMove(null);
    setMoveFailed(null);
    setReadError(null);
    setFlagError(null);
  };

  // The phone folder dropdown lists one level of subfolders too, loaded the
  // first time it is opened (the desktop tree loads them on expand).
  const loadAllChildren = async () => {
    const missing = roots.filter((f) => f.child_count && !children[f.id]);
    if (!missing.length) return;
    const loaded = await Promise.all(
      missing.map((f) =>
        api.getFolderChildren(f.id)
          .then((kids) => [f.id, kids])
          .catch(() => [f.id, []])
      )
    );
    setChildren((c) => ({ ...c, ...Object.fromEntries(loaded) }));
  };

  // Below xl the message replaces the list, so start it at the top.
  const readingRef = useRef(null);
  useEffect(() => {
    if (openId && window.innerWidth < 1280) {
      readingRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [openId]);

  // `quiet` is a message restored from the URL: one that has since moved (its
  // Graph id changes on every move) is dropped rather than shown as an error.
  // The open message's conversation, split around it.
  const folderNameOf = (id) =>
    [...roots, ...Object.values(children).flat()].find((f) => f.id === id)?.name || null;
  const threadId = paneThread.id || message?.conversation_id || null;
  // The conversation panel starts folded (2026-10-15): one line saying how
  // many messages, opened on a click. Folded again for the next conversation.
  const [threadOpen, setThreadOpen] = useState(false);
  useEffect(() => setThreadOpen(false), [threadId]);
  const thread = useConversation(api, threadId);
  const stored = threadId && thread.id === threadId ? thread.list || [] : [];
  // The stored thread, plus the message the panel was opened on if the store
  // does not list it, in date order.
  const anchor = paneThread.anchor;
  const threadList =
    stored.length && anchor?.internet_message_id &&
    !stored.some((m) => m.internet_message_id === anchor.internet_message_id)
      ? [...stored, { ...anchor, folder_name: anchor.folder_name || folderNameOf(anchor.folder_id) }].sort(
          (a, b) => String(a.received_at || '').localeCompare(String(b.received_at || ''))
        )
      : stored;
  const hereMid = paneThread.mid || message?.internet_message_id;
  const here = threadList.findIndex((m) => m.internet_message_id && m.internet_message_id === hereMid);
  const threadOlder = here >= 0 ? threadList.slice(0, here) : threadList;
  const threadNewer = here >= 0 ? threadList.slice(here + 1) : [];
  // Open a thread entry wherever it is now: the Graph id stored with it
  // changes every time the message is moved.
  const openThreadEntry = async (m) => {
    // The panel moves its highlight at once; the message below loads.
    setPaneThread((p) => ({ ...p, mid: m.internet_message_id || null }));
    // Located by the service: `id` is the message's current one.
    if (m.in_mailbox || !m.internet_message_id) return openMessage(m.id, { fromThread: true });
    try {
      const where = await api.locate(m.internet_message_id);
      return openMessage(where.id, { fromThread: true });
    } catch (err) {
      return setMessageError(
        err?.response?.status === 404
          ? 'That email is no longer in the mailbox.'
          : apiMessage(err, 'Could not find that email.')
      );
    }
  };

  // One list row. The newest of a thread carries how many of it are on this
  // page and a toggle for the rest; the rest are drawn indented beneath it.
  const renderRow = (m, { count = 1, open = false, onToggle, nested = false } = {}) => (
    <div
      key={m.id}
      onClick={() => openMessage(m.id)}
      draggable={moves && !moveBusy && !m.is_draft}
      onDragStart={moves ? (e) => startDrag(e, m) : undefined}
      onDragEnd={moves ? endDrag : undefined}
      title={moves && !m.is_draft ? 'Drag onto a folder to move it' : undefined}
      className={`cursor-pointer border-b border-gray-100 py-2 pr-3 ${nested ? 'pl-8' : 'pl-3'} ${
        openId === m.id ? 'bg-blue-50' : 'hover:bg-gray-50'
      } ${listWait || dragMsg?.id === m.id ? 'opacity-40' : ''} transition-opacity`}
    >
      <div className="flex items-center gap-2">
        {!m.is_read && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
        <span
          className={`truncate flex-1 text-sm ${m.is_read ? 'text-gray-800' : 'font-semibold text-gray-900'}`}
          title={rowParty(m, mailbox).title}
        >
          {rowParty(m, mailbox).label}
        </span>
        {!nested && (count > 1 || m.thread_count > 1) && (
          <ThreadChip
            size={Math.max(count, m.thread_count || 0)}
            onPage={count}
            open={open}
            onToggle={onToggle}
          />
        )}
        {m.flag === 'flagged' && (
          <Flag className="h-3.5 w-3.5 shrink-0 fill-red-500 text-red-600" aria-label="Flagged" />
        )}
        {m.flag === 'complete' && (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" aria-label="Flag complete" />
        )}
        {m.has_attachments && <Paperclip className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
        {everywhere && m.folder_name && (
          <span className="max-w-[8rem] shrink-0 truncate rounded bg-gray-100 px-1.5 text-xs text-gray-600">
            {m.folder_name}
          </span>
        )}
        <span className="shrink-0 text-xs text-gray-500">{fmtListDate(m.received_at)}</span>
      </div>
      <div className={`truncate text-sm ${m.is_read ? 'text-gray-700' : 'font-medium text-gray-900'}`}>
        {m.subject || '(no subject)'}
      </div>
      <div className="truncate text-sm text-gray-500">{m.preview}</div>
      {plugin.ListBadges && <plugin.ListBadges message={m} />}
    </div>
  );

  // A change to one message, on whichever list shows it.
  const patchRows = (id, update) => {
    setMessages((list) => list.map((x) => (x.id === id ? update(x) : x)));
    setFound((f) => ({ ...f, messages: f.messages.map((x) => (x.id === id ? update(x) : x)) }));
  };

  // A folder's counts moved by a read-state change or a move, wherever the
  // folder is drawn. The next refresh brings the real counts.
  const bumpFolder = (folderId, { total = 0, unread = 0 }) => {
    if (!folderId) return;
    const bump = (f) =>
      f.id === folderId
        ? {
            ...f,
            total: Math.max(0, (f.total || 0) + total),
            unread: Math.max(0, (f.unread || 0) + unread),
          }
        : f;
    setRoots((r) => r.map(bump));
    setChildren((c) => Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v.map(bump)])));
    setFolder((f) => (f ? bump(f) : f));
  };
  const bumpUnread = (folderId, delta) => bumpFolder(folderId, { unread: delta });

  const knownFolders = () => [...roots, ...Object.values(children).flat()];

  // A reply or forward draft is made here, once per click (a second click while
  // it is being made does nothing), and handed to the composer.
  const startCompose = (start) => leaveComposer(() => beginCompose(start));
  const beginCompose = async (start) => {
    if (composeStarting) return;
    setComposeNotice(null);
    setComposeError(null);
    if (start.draftId || start.kind === 'new') {
      // An existing draft opens as it is; a new message has no draft until
      // something is typed in it (the composer makes it then).
      setComposing({ ...start, at: Date.now() });
      return;
    }
    setComposeStarting(true);
    try {
      const draft = await api.createDraft(start.kind, start.messageId);
      setComposing({ draftId: draft.id, draft, replyTo: start.messageId, at: Date.now() });
      refresh(); // Drafts has one more message: show its real count now
    } catch (err) {
      setComposeError(apiMessage(err, 'Could not start the draft.'));
    } finally {
      setComposeStarting(false);
    }
  };

  // While the composer is open, the open message is not drawn under it: a
  // reply or forward quotes the original inside the composer, and the message
  // (with its card) comes back as soon as the composer closes. It stayed on
  // screen under a reply until 2026-10-15, and read as something still to do.
  const showOpen = !composing;

  // The composer closed: sent, discarded, or kept in Drafts. A sent or
  // discarded draft that was the open message no longer exists.
  const composeClosed = ({ sent, discarded, saved, dropped } = {}) => {
    const wasDraft = composing?.draftId && composing.draftId === message?.id;
    setComposing(null);
    setComposeNotice(sent ? 'Sent.' : discarded ? 'Draft discarded.' : saved ? 'Saved in Drafts.' : null);
    if ((sent || discarded) && wasDraft) {
      // The open message was that draft, and it is gone: so is its thread.
      setOpenId(null);
      setMessage(null);
      setPaneThread(NO_PANE);
    }
    if (sent || discarded || saved || dropped) refresh();
  };

  const openKey = message?.internet_message_id || null;
  useEffect(() => {
    if (!showActivity || !openKey) {
      setActivity({ key: null, list: [] });
      return undefined;
    }
    let live = true;
    api
      .history(openKey)
      .then((list) => live && setActivity({ key: openKey, list }))
      .catch(() => live && setActivity({ key: openKey, list: [] })); // activity is extra; the message stands without it
    return () => {
      live = false;
    };
  }, [openKey, activityTick]); // eslint-disable-line
  const archiveId = roots.find((f) => f.name.toLowerCase() === 'archive')?.id;

  // Move a message (the open one, or back again for Undo). The message gets a
  // new id from the move, so the open message, its row in a search and the
  // Undo all carry the new one. A folder list simply loses the row.
  // `where` is where a failure is shown: the reading pane, or above the list
  // for a row dragged from there that is not the open message.
  const moveMessage = async (m, destination, { undo = false, where = 'pane' } = {}) => {
    if (!moves || moveBusy) return;
    setMoveBusy(true);
    setMoveFailed(null);
    try {
      const moved = await api.move(m.id, destination);
      const to = knownFolders().find((f) => f.id === moved.folder_id);
      const toName = to?.name || (destination === 'archive' ? 'Archive' : 'the folder');
      if (moved.folder_id !== moved.from_folder_id) {
        const unread = m.is_read ? 0 : 1;
        bumpFolder(moved.from_folder_id, { total: -1, unread: -unread });
        bumpFolder(moved.folder_id, { total: 1, unread });
      }
      setMessages((list) => list.filter((x) => x.id !== m.id));
      setFound((f) => ({
        ...f,
        messages: f.messages.map((x) =>
          x.id === m.id
            ? { ...x, id: moved.id, folder_id: moved.folder_id, folder_name: to?.name || x.folder_name }
            : x
        ),
      }));
      setMessage((x) => (x && x.id === m.id ? { ...x, id: moved.id, folder_id: moved.folder_id } : x));
      setOpenId((id) => (id === m.id ? moved.id : id));
      setLastMove(
        undo ? null : { id: moved.id, from: moved.from_folder_id, toName, isRead: m.is_read }
      );
      setActivityTick((t) => t + 1);
      // Undo into the folder on screen: read it again so the row is back in place.
      if (undo && folder && moved.folder_id === folder.id) {
        loadMessages(folder, offset, filtersRef.current, { quiet: true });
      }
    } catch (err) {
      setMoveFailed({ text: moveError(err, 'Could not move this message.'), where });
    } finally {
      setMoveBusy(false);
    }
  };

  // Drag and drop: a list row onto a folder in the tree. One message at a time,
  // like the buttons, and through the same move, so counts, Activity and Undo
  // behave the same. Not offered while a move is in flight or for a draft.
  const stopExpandTimer = () => {
    clearTimeout(expandTimer.current);
    expandTimer.current = null;
  };
  const endDrag = () => {
    stopExpandTimer();
    setDragMsg(null);
    setDropOn(null);
  };
  useEffect(() => () => clearTimeout(expandTimer.current), []);
  const startDrag = (e, m) => {
    const from = m.folder_id || folder?.id || null;
    setDragMsg({ id: m.id, is_read: m.is_read, from });
    setMoveFailed(null);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      // Some browsers start no drag without data. The id is not read back.
      e.dataTransfer.setData('text/plain', m.subject || '(no subject)');
    }
  };
  const dropFor = (f) =>
    dragMsg
      ? {
          accepts: canMoveInto(f) && f.id !== dragMsg.from,
          over: dropOn === f.id,
          onEnter: (target) => {
            setDropOn(target.id);
            stopExpandTimer();
            // Held over a collapsed folder with subfolders: open it, as Outlook does.
            if (target.child_count && !expanded.has(target.id)) {
              expandTimer.current = setTimeout(() => toggle(target), 700);
            }
          },
          onLeave: (target) => {
            setDropOn((id) => (id === target.id ? null : id));
            stopExpandTimer();
          },
          onDrop: (target) => {
            const m = dragMsg;
            endDrag();
            moveMessage(m, target.id, { where: m.id === openId ? 'pane' : 'list' });
          },
        }
      : null;

  // Shown at once on the message, its list row and its folder's count, and put
  // back if the mailbox refuses.
  const setRead = async (m, isRead) => {
    if (!readState || m.is_read === isRead) return;
    const apply = (value) => {
      setMessage((x) => (x && x.id === m.id ? { ...x, is_read: value } : x));
      patchRows(m.id, (x) => ({ ...x, is_read: value }));
      bumpUnread(m.folder_id, value ? -1 : 1);
    };
    apply(isRead);
    setReadError(null);
    try {
      await api.setRead(m.id, isRead);
      setActivityTick((t) => t + 1);
    } catch (err) {
      apply(!isRead);
      setReadError(apiMessage(err, 'Could not change whether this message is read.'));
    }
  };

  // Shown at once on the message and its row, and put back if the mailbox refuses.
  const [flagError, setFlagError] = useState(null);
  const setFlag = async (m, flag) => {
    if (!flags || m.flag === flag) return;
    const before = m.flag || 'none';
    const apply = (value) => {
      setMessage((x) => (x && x.id === m.id ? { ...x, flag: value } : x));
      patchRows(m.id, (x) => ({ ...x, flag: value }));
    };
    apply(flag);
    setFlagError(null);
    try {
      await api.setFlag(m.id, flag);
      setActivityTick((t) => t + 1);
    } catch (err) {
      apply(before);
      setFlagError(apiMessage(err, 'Could not change the flag.'));
    }
  };

  // `fromThread`: a message picked in the open thread's panel. The panel stays;
  // anything else starts the reading pane over.
  // Opening a message from the list, a thread or a search leaves the composer:
  // unsaved changes are asked about first.
  const openMessage = (id, opts = {}) => leaveComposer(() => showMessage(id, opts));
  const showMessage = async (id, { quiet = false, fromThread = false } = {}) => {
    const token = ++messageToken.current;
    setOpenId(id);
    setHeldCard(fromThread ? message || heldCard : null);
    setMessage(null);
    if (!fromThread) setPaneThread(NO_PANE);
    setMessageError(null);
    setReadError(null);
    setMoveFailed(null);
    setFlagError(null);
    setLastMove(null);
    setComposing(null);
    setComposeNotice(null);
    setComposeError(null);
    try {
      const data = await api.getMessage(id);
      if (token !== messageToken.current) return;
      setMessage(data);
      setHeldCard(null);
      if (fromThread) {
        // Stepping through the panel never changes which conversation it shows:
        // the entry's own conversation id may be another (see NO_PANE).
        setPaneThread((p) => ({ ...p, mid: data.internet_message_id || p.mid }));
      } else {
        setPaneThread({
          id: data.conversation_id || null,
          mid: data.internet_message_id || null,
          anchor: { ...data, body: undefined, preview: '', in_mailbox: true },
        });
      }
      // Opening an unread message marks it read, as a mail client does.
      if (!data.is_read) setRead(data, true);
      // A draft opens in the composer: it is only in Drafts to be worked on.
      // Closing the composer leaves it on screen, with Edit draft to reopen.
      if (compose && data.is_draft) setComposing({ draftId: data.id, at: Date.now() });
    } catch (err) {
      if (token !== messageToken.current) return;
      setHeldCard(null);
      if (quiet) setOpenId(null);
      else setMessageError(apiMessage(err, 'Could not load this message.'));
    }
  };

  // A plugin's change to what it knows about the open message (DelAir: a marked
  // customer, a task, a booking): shown on the message and on its list row.
  const updateOpen = (update) => {
    setMessage((m) => (m ? update(m) : m));
    patchRows(openId, update);
  };

  const refresh = async (manual = false) => {
    const all = await loadTree();
    if (folder) {
      // Keep the selection, with its counts brought up to date.
      const current = all.find((f) => f.id === folder.id) || folder;
      setFolder(current);
      await loadMessages(current, offset, filtersRef.current, { quiet: !manual });
    }
    setRefreshedAt(new Date());
  };

  // The timer calls whatever refresh is current, not the one from first render.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const busyRef = useRef(false);
  busyRef.current = loadingTree || loadingList || dragging;

  // Every minute while the tab is visible. A hidden tab skips its ticks and
  // catches up as soon as it is looked at again, if a tick was missed.
  useEffect(() => {
    let last = Date.now();
    const tick = () => {
      if (document.hidden || busyRef.current) return;
      last = Date.now();
      refreshRef.current();
    };
    const timer = setInterval(tick, AUTO_REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - last >= AUTO_REFRESH_MS) tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(FULLSCREEN_KEY, fullScreen ? '1' : '0');
    } catch {
      /* not remembered (private window), which is fine */
    }
  }, [fullScreen]);

  useEffect(() => {
    try {
      localStorage.setItem(FOLDERS_SHOWN_KEY, foldersShown ? '1' : '0');
    } catch {
      /* not remembered, which is fine */
    }
  }, [foldersShown]);

  // Esc leaves full screen, except from a field, where it means something else.
  useEffect(() => {
    if (!fullScreen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      setFullScreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  const goTo = (off) => {
    setOffset(off);
    setOpenId(null);
    setMessage(null);
    loadMessages(folder, off, filtersRef.current);
  };

  // Filtered or not, a page is PAGE_SIZE of something: messages, or matches.
  const newer = () => goTo(Math.max(0, offset - PAGE_SIZE));
  const older = () => goTo(offset + PAGE_SIZE);

  // Keep every column usable whatever the window width: the list can grow only
  // as far as leaves the reading pane its minimum.
  const fit = (w) => {
    const room = gridRef.current?.clientWidth || window.innerWidth;
    const folders = clamp(w.folders, ...FOLDERS_RANGE);
    const taken = foldersShown ? folders + 12 : 0;
    const list = clamp(w.list, LIST_MIN, Math.max(LIST_MIN, room - taken - READING_MIN - 12));
    return { folders, list };
  };

  // Widths saved on a wider screen are fitted to this one, without saving the
  // squeezed result: back on the wide screen, the saved widths still apply.
  // Watched on the grid itself, not the window: leaving full screen or the
  // app's sidebar opening narrows the grid with no window resize, and the
  // reading pane was left squeezed to a sliver (2026-09-29).
  useEffect(() => {
    const refit = () => setWidths(fit(loadWidths()));
    refit();
  }, [foldersShown]); // eslint-disable-line
  useEffect(() => {
    const refit = () => setWidths(fit(loadWidths()));
    if (typeof ResizeObserver !== 'undefined' && gridRef.current) {
      let seen = gridRef.current.clientWidth;
      // Width only: below xl the grid's height changes with every load.
      const watch = new ResizeObserver(() => {
        const now = gridRef.current?.clientWidth;
        if (now === seen) return;
        seen = now;
        refit();
      });
      watch.observe(gridRef.current);
      return () => watch.disconnect();
    }
    window.addEventListener('resize', refit);
    return () => window.removeEventListener('resize', refit);
  }, []); // eslint-disable-line

  const resizer = (column) => ({
    onDrag: (dx) => {
      if (!dragFrom.current) {
        dragFrom.current = widths;
        setDragging(true);
      }
      const from = dragFrom.current;
      setWidths(fit({ ...from, [column]: from[column] + dx }));
    },
    onDragEnd: () => {
      dragFrom.current = null;
      setDragging(false);
      setWidths((w) => {
        saveWidths(w);
        return w;
      });
    },
    onReset: () => {
      const w = fit({ ...widths, [column]: DEFAULT_WIDTHS[column] });
      setWidths(w);
      saveWidths(w);
    },
    onStep: (dx) => {
      const w = fit({ ...widths, [column]: widths[column] + dx });
      setWidths(w);
      saveWidths(w);
    },
  });

  // The top-level folders the tree shows: all of them, or with the plugin's
  // scope on, its folders, the Inbox and Drafts (and the open one, so it does
  // not vanish from under the list).
  const keepNames = folderScope
    ? new Set([...EVERYDAY_FOLDERS, ...folderScope.map((n) => n.toLowerCase())])
    : null;
  // Subfolders follow the same rule as top-level ones (they did not until
  // 2026-10-15, so the Inbox's subfolders were always all listed). A parent is
  // kept on its own name, not on what it holds: its children may not be
  // loaded yet.
  const inScope = (f) =>
    !(scoped && keepNames) || keepNames.has(f.name.toLowerCase()) || f.id === folder?.id;
  const shownRoots = roots.filter(inScope);
  const shownChildren =
    scoped && keepNames
      ? Object.fromEntries(Object.entries(children).map(([id, list]) => [id, list.filter(inScope)]))
      : children;

  const renderLevel = (folders, depth) =>
    folders.map((f) => (
      <React.Fragment key={f.id}>
        <FolderRow
          folder={f}
          depth={depth}
          expanded={expanded.has(f.id)}
          selected={!everywhere && folder?.id === f.id}
          onToggle={toggle}
          onSelect={pickFolder}
          drop={dropFor(f)}
        />
        {expanded.has(f.id) && shownChildren[f.id] && renderLevel(shownChildren[f.id], depth + 1)}
      </React.Fragment>
    ));

  // Below xl the list and the reading pane take turns; the pane has the screen
  // while there is something in it (a message, the composer, a notice). On xl
  // it is always there.
  const paneOpen = Boolean(openId || composing || composeNotice || composeError || messageError);

  const filtered = pageInfo.filtered;
  // A folder's count includes items that are not email (a deleted calendar
  // entry or contact, in Deleted Items), which the message listing leaves out.
  // On the last page the emails actually listed are the true total, and the
  // difference is said, not drawn as "1–0 of 1".
  const folderTotal = folder?.total ?? 0;
  const lastPage = !filtered && !loadingList && !listError && messages.length < PAGE_SIZE;
  const total = filtered
    ? pageInfo.total_matches
    : lastPage
    ? Math.min(folderTotal, offset + messages.length)
    : folderTotal;
  const notEmail = lastPage ? Math.max(0, folderTotal - total) : 0;
  const from = total ? offset + 1 : 0;
  const to = Math.min(offset + messages.length, total || offset + messages.length);
  const hasOlder = offset + PAGE_SIZE < total;

  return (
    <div
      // On xl the page is a column the height of the viewport (less the app's
      // main padding), and the grid takes what the header leaves, whatever its
      // height; a fixed reserve left a band unused on a 768-px screen (2026-10-15).
      className={`${plugin.className || ''} xl:flex xl:flex-col ${
        fullScreen ? 'fixed inset-0 z-40 overflow-auto bg-gray-50 p-3' : 'xl:h-[calc(100vh-3rem)]'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Mailbox</h1>
          <p className="mt-1 hidden text-sm text-gray-600 sm:block">
            {mailbox ? <span className="font-medium">{mailbox}</span> : 'The service mailbox'}, live
            from Microsoft Graph.{' '}
            {moves
              ? 'Moves and read state changed here are recorded with your name.'
              : readState
              ? 'Nothing here moves mail.'
              : 'Nothing here moves mail or marks it read.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {compose && (
            <button
              type="button"
              onClick={() => startCompose({ kind: 'new' })}
              disabled={composeStarting}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <PenSquare className="mr-1.5 h-4 w-4" />
              New message
            </button>
          )}
          <button
            type="button"
            onClick={() => refresh(true)}
            disabled={loadingTree}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loadingTree ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setFullScreen((f) => !f)}
            className="hidden items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50 md:inline-flex"
            aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
            title={fullScreen ? 'Exit full screen (Esc)' : 'Full screen'}
          >
            {fullScreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* The dividers are grid columns of their own on xl; below it everything
          stacks and they are hidden. While dragging, the reading pane's iframe
          must not swallow the pointer. */}
      <div
        ref={gridRef}
        style={{
          '--mb-folders': `${widths.folders}px`,
          '--mb-list': `${widths.list}px`,
        }}
        className={`grid grid-cols-1 gap-3 xl:min-h-0 xl:flex-1 ${
          foldersShown
            ? 'xl:grid-cols-[var(--mb-folders)_12px_var(--mb-list)_12px_minmax(0,1fr)]'
            : 'xl:grid-cols-[var(--mb-list)_12px_minmax(0,1fr)]'
        } xl:gap-0 ${
          dragging ? 'cursor-col-resize select-none [&_iframe]:pointer-events-none' : ''
        }`}
      >
        {/* Folders */}
        {/* Below xl, or hidden, the tree is the dropdown in the list header instead. */}
        {foldersShown && (
        <div
          aria-label="Folders"
          className="hidden rounded-md border border-gray-200 bg-white p-2 overflow-auto xl:block"
        >
          {treeError && <div className="mb-2 text-sm text-red-700">{treeError}</div>}
          {keepNames && (
            <label className="mb-2 flex cursor-pointer items-center gap-1.5 px-1 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={scoped}
                onChange={(e) => setScoped(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300"
              />
              {plugin.folderScopeLabel || 'Only the main folders'}
            </label>
          )}
          {loadingTree && !roots.length ? (
            <div className="p-2 text-sm text-gray-500">Loading folders…</div>
          ) : (
            renderLevel(shownRoots, 0)
          )}
        </div>
        )}

        {foldersShown && <Splitter label="Resize folders" {...resizer('folders')} />}

        {/* Message list */}
        {/* Below xl the list and the message take turns: one pane at a time. */}
        <div
          className={`${paneOpen ? 'hidden xl:flex' : 'flex'} flex-col min-h-0 rounded-md border border-gray-200 bg-white`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
            {/* The folder controls sit together on the left; the count on the right. */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFoldersShown((v) => !v)}
              aria-label={foldersShown ? 'Hide folders' : 'Show folders'}
              title={foldersShown ? 'Hide the folder list' : 'Show the folder list'}
              aria-pressed={foldersShown}
              className="hidden shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 xl:inline-flex"
            >
              {foldersShown ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </button>
            {foldersShown && (
              <div className="hidden truncate text-sm font-medium text-gray-800 xl:block">
                {everywhere ? 'All folders' : folder?.name || ' '}
              </div>
            )}
            <FolderPicker
              wide={!foldersShown}
              roots={shownRoots}
              subfolders={shownChildren}
              current={folder}
              onOpen={loadAllChildren}
              onPick={(id) => {
                const f = [...roots, ...Object.values(children).flat()].find((x) => x.id === id);
                if (f) pickFolder(f);
              }}
            />
            {/* With the tree hidden, its folder-scope choice moves up here. */}
            {!foldersShown && keepNames && (
              <label className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-gray-600 xl:flex">
                <input
                  type="checkbox"
                  checked={scoped}
                  onChange={(e) => setScoped(e.target.checked)}
                  aria-label={plugin.folderScopeLabel || 'Only the main folders'}
                  className="h-3.5 w-3.5 rounded border-gray-300"
                />
                {plugin.folderScopeLabel || 'Only the main folders'}
              </label>
            )}
            </div>
            {everywhere ? (
              <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">
                {found.loading && !found.messages.length
                  ? 'Searching…'
                  : `${found.messages.length.toLocaleString()}${found.next_cursor ? '+' : ''} result${
                      found.messages.length === 1 && !found.next_cursor ? '' : 's'
                    }`}
              </span>
            ) : (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              {/* Filtered: a count of matches across the whole folder, and only
                  when the folder was too big to read to the end, how much was. */}
              <span
                className="whitespace-nowrap tabular-nums"
                title={
                  filtered && !pageInfo.complete
                    ? `Only the newest ${pageInfo.scanned.toLocaleString()} messages of this folder were searched`
                    : undefined
                }
              >
                {filtered
                  ? `${from}–${to} of ${total.toLocaleString()} match${total === 1 ? '' : 'es'}${
                      pageInfo.complete ? '' : ` · newest ${pageInfo.scanned.toLocaleString()}`
                    }`
                  : total
                  ? `${from}–${to} of ${total}`
                  : '0'}
                {notEmail > 0 && (
                  <span
                    className="text-gray-400"
                    title="Items such as calendar entries or contacts are counted in the folder but can’t be shown here"
                  >
                    {` · ${notEmail} other item${notEmail === 1 ? '' : 's'}`}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                disabled={!folder || offset === 0 || loadingList}
                onClick={newer}
                aria-label="Newer"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                disabled={!folder || !hasOlder || loadingList}
                onClick={older}
                aria-label="Older"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            )}
          </div>
          <FilterBar
            filters={filters}
            search={search}
            plugin={plugin}
            controls={
              plugin.FilterControls && (
                <plugin.FilterControls
                  filters={filters}
                  options={filterOptions}
                  onChange={changeFilters}
                />
              )
            }
            scope={scope}
            onScope={changeScope}
            onSearch={setSearch}
            onChange={changeFilters}
            onClear={clearFilters}
          />
          <div className="overflow-auto flex-1" aria-busy={Boolean(listWait)}>
            {everywhere && found.loading && (
              <ListProgress
                wait={{ since: found.since, filtered: true, everywhere: true }}
                folder={folder}
                overlay={found.messages.length > 0}
              />
            )}
            {everywhere && found.error && <div className="p-3 text-sm text-red-700">{found.error}</div>}
            {everywhere && found.loading && !found.messages.length && <SkeletonRows />}
            {everywhere && !found.loading && !found.error && !found.messages.length && (
              <div className="p-3 text-sm text-gray-500">No messages match in any folder.</div>
            )}
            {scope === 'all' && !everywhere && (
              <div className="p-3 text-sm text-gray-500">
                Type at least two characters, or choose Flagged, to search every folder.
              </div>
            )}
            {!everywhere && listWait && (
              <ListProgress wait={listWait} folder={folder} overlay={messages.length > 0} />
            )}
            {!everywhere && listError && <div className="p-3 text-sm text-red-700">{listError}</div>}
            {!everywhere && loadingList && !messages.length && <SkeletonRows />}
            {scope === 'folder' && !loadingList && !listError && folder && !messages.length && !filtered && (
              <div className="p-3 text-sm text-gray-500">
                {notEmail > 0
                  ? `No emails in this folder. ${
                      notEmail === 1
                        ? '1 other item, such as a calendar entry or contact, can’t'
                        : `${notEmail} other items, such as calendar entries or contacts, can’t`
                    } be shown here.`
                  : 'This folder is empty.'}
              </div>
            )}
            {scope === 'folder' && !loadingList && !listError && folder && !messages.length && filtered && (
              <div className="p-3 text-sm text-gray-500">
                No messages match
                {filterWords(filters, plugin).length > 0 && (
                  <>
                    {' '}
                    the filter{filterWords(filters, plugin).length > 1 ? 's' : ''}{' '}
                    <span className="font-medium text-gray-700">
                      {filterWords(filters, plugin).join(' · ')}
                    </span>
                  </>
                )}
                {pageInfo.complete
                  ? '.'
                  : ` in the newest ${pageInfo.scanned.toLocaleString()} of this folder.`}{' '}
                <button type="button" onClick={clearFilters} className="text-blue-700 hover:underline">
                  Clear filters
                </button>
              </div>
            )}
            {/* A row dragged from the list that is not the open message: its
                Undo and any failure show here, where the person is looking. */}
            {lastMove && lastMove.id !== message?.id && (
              <div className="px-3 pt-2">
                <UndoBar
                  move={lastMove}
                  busy={moveBusy}
                  onUndo={() =>
                    moveMessage({ id: lastMove.id, is_read: lastMove.isRead }, lastMove.from, {
                      undo: true,
                      where: 'list',
                    })
                  }
                  onDismiss={() => setLastMove(null)}
                />
              </div>
            )}
            {moveFailed && moveFailed.where === 'list' && (
              <div className="px-3 pt-2 text-xs text-red-700">{moveFailed.text}</div>
            )}
            {groupByConversation(everywhere ? found.messages : scope === 'all' ? [] : messages).map((group) => {
              const [latest, ...older] = group;
              const key = latest.conversation_id || latest.id;
              const open = openThreads.has(key);
              const active = group.some((m) => m.id === openId);
              return (
                <div key={latest.id} className={active && older.length ? 'bg-blue-50/40' : ''}>
                  {renderRow(latest, {
                    count: group.length,
                    open,
                    onToggle: () =>
                      setOpenThreads((s) => {
                        const next = new Set(s);
                        if (next.has(key)) next.delete(key);
                        else next.add(key);
                        return next;
                      }),
                  })}
                  {open && older.map((m) => renderRow(m, { nested: true }))}
                </div>
              );
            })}
            {everywhere && found.next_cursor && found.messages.length > 0 && (
              <div className="p-3 text-center">
                <button
                  type="button"
                  onClick={() => runSearch(found.query, found.next_cursor)}
                  disabled={found.loading}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {found.loading ? 'Loading…' : 'More results'}
                </button>
              </div>
            )}
          </div>
        </div>

        <Splitter label="Resize message list" {...resizer('list')} />

        {/* Reading pane */}
        <div
          ref={readingRef}
          className={`${paneOpen ? 'block' : 'hidden xl:block'} min-h-[16rem] overflow-auto rounded-md border border-gray-200 bg-white p-3 sm:p-4`}
        >
          {/* Back to the list below xl, with the × at the far right: on a small
              screen this line is where it lives, not on the card. */}
          <div className="mb-3 flex items-center xl:hidden">
            <button
              type="button"
              onClick={closeMessage}
              className="inline-flex items-center text-sm text-blue-700 hover:underline"
            >
              <ChevronLeft className="mr-0.5 h-4 w-4" />
              Messages
            </button>
            {openId && !composing && (
              <button
                type="button"
                onClick={closeMessage}
                aria-label="Close message"
                title="Close"
                className="ml-auto shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {composing && (
            <div className="mb-4">
              <Composer
                ref={composerRef}
                key={composing.at}
                api={api}
                start={composing}
                onClose={composeClosed}
                onCreated={() => refresh()}
              />
            </div>
          )}
          {composeError && !composing && (
            <div className="mb-3 text-sm text-red-700">{composeError}</div>
          )}
          {composeNotice && !composing && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-sm text-green-900">
              <span className="flex-1">{composeNotice}</span>
              <button
                type="button"
                onClick={() => setComposeNotice(null)}
                className="rounded px-1 text-green-800 hover:bg-green-100"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}
          {!paneOpen && (
            <Overview
              api={api}
              folder={folder}
              tick={refreshedAt}
              query={toQuery(filters, plugin)}
              Extra={plugin.Overview}
              onFilter={(patch) => {
                // The overview counts under the filters in force (2026-10-15),
                // so a category or customer picked there narrows them further.
                if (scopeRef.current === 'all') changeScope('folder');
                applyFilters({ ...filtersRef.current, ...patch });
              }}
            />
          )}
          {messageError && <div className="text-sm text-red-700">{messageError}</div>}
          {/* The plugin's card (category, summary, customer) above the
              conversation, so it is the first thing seen on a threaded message. */}
          {/* × closes the open message the way the composer's × closes a draft,
              and the overview returns. A plugin card draws it at the end of its
              own first line (its `actions`), so the card takes the full width;
              without one it stands on its own. */}
          {showOpen &&
            openId &&
            (() => {
              const closeButton = composing ? null : (
                <button
                  type="button"
                  onClick={closeMessage}
                  aria-label="Close message"
                  title="Close"
                  className="hidden shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 xl:inline-flex"
                >
                  <X className="h-4 w-4" />
                </button>
              );
              if ((message || heldCard) && plugin.MessageCard) {
                return (
                  <div
                    className={`mb-2 transition-opacity ${message ? '' : 'pointer-events-none opacity-50'}`}
                  >
                    <plugin.MessageCard
                      key={`card:${messageKey(message || heldCard)}`}
                      message={message || heldCard}
                      onUpdate={updateOpen}
                      actions={closeButton}
                    />
                  </div>
                );
              }
              return closeButton && <div className="mb-3 hidden justify-end xl:flex">{closeButton}</div>;
            })()}
          {/* Only with a message open: the panel is that message's thread, and
              must not outlive it (it once stayed above the overview after an
              open draft was discarded). */}
          {showOpen && openId && threadList.length > 1 && (
            <div className="mb-3 overflow-hidden rounded-md border border-gray-200">
              <button
                type="button"
                onClick={() => setThreadOpen((o) => !o)}
                aria-expanded={threadOpen}
                className={`flex w-full items-center gap-1.5 bg-gray-50 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100 ${
                  threadOpen ? 'border-b border-gray-200' : ''
                }`}
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-150 ${
                    threadOpen ? '' : '-rotate-90'
                  }`}
                />
                <Users className="h-3.5 w-3.5" />
                Conversation · {threadList.length} messages
                {threadList.length >= 50 && <span className="font-normal normal-case">(first 50)</span>}
              </button>
              {threadOpen && (
                <>
                  {threadOlder.map((m) => (
                    <ThreadRow key={m.id} m={m} onOpen={openThreadEntry} Badge={plugin.ThreadBadge} />
                  ))}
                  {(here >= 0 || message) && (
                    <ThreadRow
                      Badge={plugin.ThreadBadge}
                      m={here >= 0 ? threadList[here] : message}
                      current
                    />
                  )}
                  {threadNewer.map((m) => (
                    <ThreadRow key={m.id} m={m} onOpen={openThreadEntry} Badge={plugin.ThreadBadge} />
                  ))}
                </>
              )}
            </div>
          )}
          {showOpen && thread.error && threadId && thread.id === threadId && (
            <div className="mb-2 text-xs text-red-700">{thread.error}</div>
          )}
          {showOpen && openId && !message && !messageError && <MessageSkeleton />}
          {showOpen && message && lastMove && lastMove.id === message.id && (
            <UndoBar
              move={lastMove}
              busy={moveBusy}
              onUndo={() =>
                moveMessage({ id: lastMove.id, is_read: message.is_read }, lastMove.from, {
                  undo: true,
                })
              }
              onDismiss={() => setLastMove(null)}
            />
          )}
          {showOpen && message && (
            <div>
              {/* Reply, Reply all and Forward, then Archive, Move to… and read
                  state as icons at the row's end. */}
              {!composing && (compose || moves || readState) && (
                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  {!compose ? null : message.is_draft ? (
                    <button
                      type="button"
                      onClick={() => startCompose({ draftId: message.id })}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      <PenSquare className="h-4 w-4" />
                      Edit draft
                    </button>
                  ) : (
                    [
                      ['reply', 'Reply', Reply],
                      ['reply_all', 'Reply all', ReplyAll],
                      ['forward', 'Forward', Forward],
                    ].map(([kind, label, Icon]) => (
                      <button
                        key={kind}
                        type="button"
                        onClick={() => startCompose({ kind, messageId: message.id })}
                        disabled={composeStarting}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))
                  )}
                  <div className="ml-auto flex shrink-0 items-center gap-0.5">
                    {moves && (
                      <MoveControls
                        compact
                        message={message}
                        choices={folderChoices(roots, children)}
                        archiveId={archiveId}
                        busy={moveBusy}
                        onMove={(dest) => moveMessage(message, dest)}
                        onOpenPicker={loadAllChildren}
                      />
                    )}
                    {readState && (
                      <button
                        type="button"
                        onClick={() => setRead(message, !message.is_read)}
                        title={message.is_read ? 'Mark unread' : 'Mark read'}
                        className="inline-flex shrink-0 items-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      >
                        {message.is_read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                        <span className="sr-only">{message.is_read ? 'Mark unread' : 'Mark read'}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-4 flex items-start gap-1">
                <h2 className="min-w-0 text-lg font-semibold text-gray-900 break-words">
                  {message.subject || '(no subject)'}
                </h2>
                <FlagControl
                  message={message}
                  canSet={Boolean(flags)}
                  onSet={(value) => setFlag(message, value)}
                />
              </div>
              {readError && <div className="mt-1 text-xs text-red-700">{readError}</div>}
              {moveFailed && moveFailed.where === 'pane' && (
                <div className="mt-1 text-xs text-red-700">{moveFailed.text}</div>
              )}
              {flagError && <div className="mt-1 text-xs text-red-700">{flagError}</div>}
              <dl className="mt-2 grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-0.5 text-sm">
                <dt className="text-gray-500">From</dt>
                <dd className="text-gray-900 break-words">{whoFull(message.sender)}</dd>
                <dt className="text-gray-500">To</dt>
                <dd className="text-gray-700 break-words">{message.to.map(whoFull).join(', ')}</dd>
                {message.cc.length > 0 && (
                  <>
                    <dt className="text-gray-500">Cc</dt>
                    <dd className="text-gray-700 break-words">{message.cc.map(whoFull).join(', ')}</dd>
                  </>
                )}
                <dt className="text-gray-500">Received</dt>
                <dd className="text-gray-700">{fmtFullDate(message.received_at)}</dd>
              </dl>
              <Attachments key={`attachments:${messageKey(message)}`} api={api} message={message} />
              <div className="mt-4">
                <EmailBody
                  key={`body:${messageKey(message)}`}
                  body={message.body}
                  textClassName="whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-3 text-base leading-relaxed text-gray-900"
                  frameClassName="w-full h-[620px] rounded border border-gray-200 bg-white"
                  readable
                  maxChars={null}
                  imagesToggle
                />
              </div>
              {activity.key === openKey && (
                <Activity
                  key={openKey}
                  entries={activity.list}
                  folderName={(id) => knownFolders().find((f) => f.id === id)?.name || null}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MailboxPage;
